import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { useScriptCreation } from '../../contexts/ScriptCreationContext';
import { useWebAudio } from '../../hooks/useWebAudio';
import { useAudioLibrary } from '../../hooks/useAudioLibrary';
import { useUser } from '../../contexts/UserContext';
import { scriptService } from '../../services/scriptService';
import { Icon } from '../../components/common/Icon';
import AudioTimeline from '../../components/audio/AudioTimeline';
import { BrainwaveGeneratorV2 } from '../../components/audio/BrainwaveGeneratorV2';
import { WhiteNoiseGenerator } from '../../components/audio/WhiteNoiseGenerator';
import { BrainwaveSettings } from '../../hooks/useWebAudio';
import { NoiseSettings } from '../../utils/audio/WhiteNoiseGeneratorNode';
import { FaPlay, FaPause, FaStop, FaVolumeUp, FaPlus, FaMusic, FaTree, FaBell, FaUpload, FaTimes, FaSave, FaTrash, FaSearch, FaThumbsUp, FaThumbsDown, FaRocket, FaRedoAlt } from 'react-icons/fa';
import { config } from '../../config';
import { generateWaveform } from '../../utils/audio/waveformGenerator';
import { processAndUploadAudio, saveAudioMetadata } from '../../utils/audio/audioUploadProcessor';
import { audioLibraryService } from '../../services/audioLibraryService';
import { HelpButton } from '../../components/common/HelpButton';

const helpContent = `
<div class="alert info">
   Customize your meditation by adding music, background ambiance, brainwave entrainment, and white/brown/pink noise.
   </br></br>
   <h3>Audio Mixer</h3>
<ul>
<li>Click and drag an audio track to change its position in the timeline.</li>
<li>Click and drag the right edge of an audio track to trim the track.</li>
<li>Click and drag the top corners of an audio track to fade in and out.</li>
<li>Right click on an audio track (other than the main track) to duplicate or loop the track.</li>
<li>Each audio track has a volume control to adjust the volume of the track.</li>
</ul>
<h3>Audio Library</h3>
<ul>
<li>Audio library tracks can be searched, sorted, and filtered by length.</li>
<li>Audio tracks can be filtered by type (Music, Ambient, Effects).</li>
<li>Click on the "Play" button to preview the audio track.</li>
<li>Click the "+" button to add the audio track to the timeline.</li>
<li>click the "Upload Audio" button to upload your own audio tracks to the library.</li>
</ul>
<h3>Brainwave Entrainment Generator</h3>
Brainwave entrainment is a technique that uses rhythmic sound to guide the brain's electrical activity into a specific frequency pattern. 
The goal is to encourage the brain to match (or "entrain" to) the rhythm, which can influence mental states like relaxation, focus, or sleep.</br></br>
Since our ears can not hear ultra low frequencies (such as 6hz), the brainwave entrainment generator can use multiple "modulation types" create special sounds that we do interpret as those low frequencies.</br></br>

<ul>
<li>Binaural Beats - Different frequency in each ear, which the brain interprets as a single frequency of the difference between the two frequencies. (Best for use with headphones)</li>
<li>Amplitude Modulation - Volume pulses at the desired frequency. (Can be used with single speaker)</li>
<li>Frequency Modulation - Pitch varies (higher and lower pitch) at the desired frequency. (Can be used with single speaker)</li>
<li>Stereo Modulation - Tone alternates left to right at the desired frequency.(Best for use with stereo speakers or headphones)</li>
<li>Isochronic Tones - Sharp rhythmic pulses, works well, but most harsh on the ears.</li>
<li>Carrier Frequency - This is the frequency of the sound that is being modulated. Choose the pitch that sounds most pleasant to you.  
There is evidence that there are ideal frequencies for each entrainment frequency (search "Oster Curve"), but often the tone that sounds pleasant to you, that 
you will actually listen to, can be just as effective as the "ideal" frequency.</li>
<li>Brainwave Frequency - This is the target frequency that you want your brain to entrain to, depending on if you want to relax, focus, sleep, etc.</li>

</ul>
<h3>White Noise Generator</h3>
White, brown, and pink noise can help with meditation by masking distracting sounds, promoting relaxation, and creating a consistent auditory background that makes it easier to focus and stay present.</br></br>
<ul>
<li>White Noise - High frequency noise, which is most effective at masking distracting sounds.</li>
<li>Brown Noise - Lower frequency noise, which is most effective at promoting relaxation.</li>
<li>Pink Noise - Medium frequency noise, which is most effective at creating a consistent auditory background that makes it easier to focus and stay present.</li>

</ul>
</div>

`;
const Container = styled.div`
  //max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Header = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.h2`
  color: #ffffff;
  margin-bottom: 10px;
  font-size: 24px;
`;

const Description = styled.p`
  color: #cccccc;
  font-size: 16px;
  margin-bottom: 20px;
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  min-height: 600px;
`;

const TimelineSection = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #333;
  width: 100%;
  
`;

const LibrarySection = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #333;
`;

const BrainwaveSection = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #333;
  margin-top: 20px;
`;

const NoiseSection = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #333;
  margin-top: 20px;
`;

const SectionTitle = styled.h3`
  color: #ffffff;
  margin-bottom: 15px;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PlaybackControls = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  padding: 15px;
  background: #2a2a2a;
  border-radius: 8px;
`;

const ControlButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'accent' }>`
  background: ${props => 
    props.$variant === 'primary' ? '#4caf50' : 
    props.$variant === 'accent' ? '#2196f3' :
    '#333'
  };
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: ${props => 
      props.$variant === 'primary' ? '#45a049' : 
      props.$variant === 'accent' ? '#1976d2' :
      '#444'
    };
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TimeDisplay = styled.div`
  color: #ffffff;
  font-family: monospace;
  font-size: 14px;
`;

const VolumeControl = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
`;

const VolumeSlider = styled.input`
  width: 80px;
  accent-color: #4caf50;
`;

const TracksContainer = styled.div`
  min-height: 400px;
  background: #111;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #333;
`;

const TrackRow = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 10px;
  margin-bottom: 10px;
  background: #222;
  border-radius: 6px;
  border: 1px solid #333;
`;

const TrackInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TrackName = styled.div`
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TrackType = styled.div`
  color: #888;
  font-size: 12px;
`;

const TrackControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const MuteButton = styled.button<{ $muted?: boolean }>`
  background: ${props => props.$muted ? '#f44336' : '#333'};
  color: white;
  border: none;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$muted ? '#d32f2f' : '#444'};
  }
`;

const TrackVolumeSlider = styled.input`
  width: 60px;
  accent-color: #4caf50;
`;

const LibraryTabs = styled.div`
  display: flex;
  gap: 5px;
  margin-bottom: 15px;
`;

const LibraryTab = styled.button<{ $active?: boolean }>`
  background: ${props => props.$active ? '#4caf50' : '#333'};
  color: white;
  border: none;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$active ? '#45a049' : '#444'};
  }
`;

const LibraryItemsList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #2a2a2a;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #666;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #888;
  }
`;

const LibraryItem = styled.div`
  padding: 12px;
  background: #2a2a2a;
  border-radius: 6px;
  border: 1px solid #333;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  overflow: hidden;

  /* Make buttons semi-transparent by default */
  button, span[role="button"] {
    opacity: 0.0;
    transition: opacity 0.2s;
  }

  &:hover {
    background: #333;
    
    /* Make buttons fully opaque on hover */
    button, span[role="button"] {
      opacity: 1;
    }
  }
`;

const WaveformOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: 100%;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0.7;
  z-index: 0;
  transition: opacity 0.2s;

  ${LibraryItem}:hover & {
    opacity: 0.4;
  }
`;

const LibraryItemContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
`;

const LibraryItemTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const LibraryItemName = styled.div`
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const LibraryItemDescription = styled.div`
  color: #ccc;
  font-size: 12px;
  line-height: 1.3;
  max-height: 2.6em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const LibraryItemActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
`;

const LibraryItemInfo = styled.div`
  color: #888;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.5);
  background-color: rgba(0, 0, 0, 0.5);
  padding: 4px;
  border-radius: 4px;
`;

const PlayButton = styled.button<{ $playing?: boolean }>`
  background: ${props => props.$playing ? '#f44336' : '#4caf50'};
  color: white;
  border: none;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$playing ? '#d32f2f' : '#45a049'};
  }
`;

const RatingButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RatingButton = styled.button<{ $active?: boolean; $type: 'like' | 'dislike' }>`
  background: ${props => props.$active 
    ? (props.$type === 'like' ? '#4caf50' : '#f44336')
    : 'transparent'
  };
  color: ${props => props.$active 
    ? 'white'
    : (props.$type === 'like' ? '#4caf50' : '#f44336')
  };
  border: 1px solid ${props => props.$type === 'like' ? '#4caf50' : '#f44336'};
  padding: 4px 6px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 10px;
  display: flex;
  align-items: center;
  gap: 2px;
  transition: all 0.2s;
  min-width: 32px;
  justify-content: center;

  &:hover {
    background: ${props => props.$type === 'like' ? '#4caf50' : '#f44336'};
    color: white;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #888;
`;

const EmptyStateText = styled.p`
  margin-bottom: 20px;
`;

const AddTrackButton = styled.button`
  background: #4caf50;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: #45a049;
  }
`;

const LibraryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  flex-wrap: wrap;
  gap: 15px;
`;

const LibraryHeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 10px;
`;

const LibraryControls = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
  width: 100%;
`;

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 300px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px 8px 36px;
  border: 1px solid #333;
  border-radius: 6px;
  background: #2a2a2a;
  color: #ffffff;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #4caf50;
  }
  
  &::placeholder {
    color: #888;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  color: #888;
  font-size: 14px;
  pointer-events: none;
`;

const SortContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SortLabel = styled.span`
  color: #ffffff;
  font-size: 14px;
  white-space: nowrap;
`;

const SortSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #333;
  border-radius: 6px;
  background: #2a2a2a;
  color: #ffffff;
  font-size: 14px;
  min-width: 160px;
  
  &:focus {
    outline: none;
    border-color: #4caf50;
  }
`;

const UploadButton = styled.button`
  background: #2196f3;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: #1976d2;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #333;
  min-width: 500px;
  max-width: 600px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h3`
  color: #ffffff;
  margin: 0;
  font-size: 18px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #888;
  font-size: 20px;
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: #fff;
  }
`;

const FormField = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  color: #ffffff;
  font-size: 14px;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #333;
  border-radius: 6px;
  background: #2a2a2a;
  color: #ffffff;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #4caf50;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #333;
  border-radius: 6px;
  background: #2a2a2a;
  color: #ffffff;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  
  &:focus {
    outline: none;
    border-color: #4caf50;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #333;
  border-radius: 6px;
  background: #2a2a2a;
  color: #ffffff;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #4caf50;
  }
`;

const FileInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #333;
  border-radius: 6px;
  background: #2a2a2a;
  color: #ffffff;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #4caf50;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const SecondaryButton = styled.button`
  background: #333;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #444;
  }
`;

const PrimaryButton = styled.button`
  background: #4caf50;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #45a049;
  }
  
  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: #f44336;
  color: white;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  background: #4caf50;
  color: white;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const UploadProgress = styled.div`
  background: #2a2a2a;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
`;

const ProgressBar = styled.div`
  background: #333;
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const ProgressFill = styled.div<{ $progress: number }>`
  background: #4caf50;
  height: 100%;
  width: ${props => props.$progress}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  color: #ffffff;
  font-size: 12px;
  text-align: center;
`;

const SaveControls = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  padding: 15px;
  background: #2a2a2a;
  border-radius: 8px;
  border: 1px solid #333;
`;

const SaveButton = styled.button<{ $variant?: 'save' | 'clear' }>`
  background: ${props => props.$variant === 'clear' ? '#f44336' : '#4caf50'};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$variant === 'clear' ? '#d32f2f' : '#45a049'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SaveStatus = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const SaveStatusText = styled.div<{ $type?: 'success' | 'error' | 'info' }>`
  font-size: 12px;
  color: ${props => 
    props.$type === 'success' ? '#4caf50' :
    props.$type === 'error' ? '#f44336' :
    '#888'
  };
`;

const AutoSaveToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

const ToggleLabel = styled.label`
  color: #ffffff;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ToggleInput = styled.input`
  accent-color: #4caf50;
`;

const WaveformCanvas = styled.canvas`
  width: 100%;
  height: 60px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  margin-bottom: 12px;
`;

const AudioPreviewControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const AudioDuration = styled.span`
  color: #888;
  font-size: 14px;
  margin-left: auto;
`;

const PreviewButton = styled.button`
  background: #333;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: #444;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CheckboxField = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const CheckboxInput = styled.input`
  margin-top: 2px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  color: #ffffff;
  font-size: 14px;
  line-height: 1.4;
  cursor: pointer;
  flex: 1;
`;

const DurationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DurationLabel = styled.label`
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
`;

const DurationSelect = styled.select`
  padding: 8px 12px;
  background: #333;
  color: white;
  border: 1px solid #555;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #4caf50;
  }
`;

const MyUploadsToggle = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: ${props => props.active ? '#4caf50' : '#333'};
  color: white;
  border: 1px solid ${props => props.active ? '#4caf50' : '#555'};
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;

  &:hover {
    background: ${props => props.active ? '#45a049' : '#444'};
  }
`;

const PrivateTag = styled.span`
  background: #ff9800;
  color: white;
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  margin-left: 8px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  padding: 16px;
  background: #1a1a1a;
  border-radius: 8px;
`;

const PaginationButton = styled.button<{ disabled?: boolean }>`
  padding: 8px 16px;
  background: ${props => props.disabled ? '#333' : '#4caf50'};
  color: ${props => props.disabled ? '#666' : 'white'};
  border: none;
  border-radius: 4px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  font-size: 14px;
  
  &:hover:not(:disabled) {
    background: #45a049;
  }
`;

const PaginationInfo = styled.span`
  color: #ccc;
  font-size: 14px;
`;

const FinalizeSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #333;
`;

const FinalizeButton = styled.button<{ $disabled?: boolean; $loading?: boolean }>`
  background: ${props => props.$disabled ? '#666' : '#ff6b35'};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  min-width: 160px;
  justify-content: center;

  &:hover {
    background: ${props => props.$disabled ? '#666' : '#e85d31'};
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const FinalizeProgress = styled.div`
  background: #2a2a2a;
  border-radius: 6px;
  padding: 12px 16px;
  border: 1px solid #333;
  min-width: 300px;
`;

const FinalizeStepContainer = styled.div`
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const FinalizeStepLabel = styled.div<{ $active?: boolean; $complete?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 12px;
  color: ${props => props.$complete ? '#4caf50' : props.$active ? '#ffffff' : '#888'};
  font-weight: ${props => props.$active ? '500' : '400'};
`;

const FinalizeStepName = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FinalizeStepIcon = styled.span<{ $complete?: boolean }>`
  color: ${props => props.$complete ? '#4caf50' : 'inherit'};
`;

const FinalizeProgressBar = styled.div`
  background: #333;
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
  position: relative;
`;

const FinalizeProgressFill = styled.div<{ $progress: number; $active?: boolean; $complete?: boolean; $indeterminate?: boolean }>`
  background: ${props => props.$complete ? '#4caf50' : props.$active ? '#ff6b35' : '#666'};
  height: 100%;
  width: ${props => props.$indeterminate ? '100%' : `${props.$progress}%`};
  transition: width 0.3s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.$indeterminate 
      ? 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)'
      : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)'};
    animation: ${props => props.$active ? (props.$indeterminate ? 'indeterminate 1.5s infinite' : 'shimmer 1.5s infinite') : 'none'};
    ${props => props.$indeterminate ? 'width: 30%;' : ''}
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
  
  @keyframes indeterminate {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(400%); }
  }
`;

const FinalizeStepProgress = styled.span`
  font-size: 11px;
  color: inherit;
  font-family: monospace;
`;

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const Audio: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const { 
    scriptAudioFile, 
    scriptName, 
    scriptDbId,
    audioMixData, 
    isAudioMixSaved, 
    audioMixError,
    saveAudioMix, 
    loadAudioMix,
    setScriptFinalAudioFile,
    generateScriptMetadata,
    generateUnguidedMetadata,
    scriptData,
    selectedParts,
    saveScript,
    setCurrentMixState
  } = useScriptCreation();
  
  
  // Track whether main track has been loaded to prevent reloading
  const mainTrackLoadedRef = useRef<string | null>(null);
  
  const [selectedLibraryTab, setSelectedLibraryTab] = useState<'music' | 'background' | 'sound'>('music');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortOption, setSortOption] = useState<'popular' | 'recent' | 'rating' | 'my_favorites'>('popular');
  const [durationFilter, setDurationFilter] = useState<'all' | 'under_1' | '1_to_2' | '2_to_5' | '5_to_10' | 'over_10'>('all');
  const [myUploadsOnly, setMyUploadsOnly] = useState<boolean>(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [playingAudio, setPlayingAudio] = useState<{ id: string; audio: HTMLAudioElement } | null>(null);
  const [shouldScrollToStart, setShouldScrollToStart] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    audio_type: 'music' as 'music' | 'background' | 'sound',
    is_private: false,
    copyright_confirmation: false,
    file: null as File | null
  });
  const [audioPreview, setAudioPreview] = useState<{
    audio: HTMLAudioElement | null;
    duration: number;
    isPlaying: boolean;
  }>({ audio: null, duration: 0, isPlaying: false });
  const waveformCanvasRef = useRef<HTMLCanvasElement>(null);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const uploadProgressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Finalize audio state
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  const [finalizeSteps, setFinalizeSteps] = useState({
    rendering: { progress: 0, active: false, complete: false, indeterminate: false },
    compressing: { progress: 0, active: false, complete: false, indeterminate: false },
    uploading: { progress: 0, active: false, complete: false, indeterminate: false },
    metadata: { progress: 0, active: false, complete: false, indeterminate: false }
  });
  
  // Audio mix save/load state
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false); // Start disabled until initial load completes
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const {
    audioContext,
    isInitialized,
    isPlaying,
    currentTime,
    duration,
    tracks,
    masterVolume,
    isLooping,
    initializeAudio,
    addTrack,
    loadTrackAudio,
    loadTrackAudioBuffer,
    updateTrack,
    removeTrack,
    setMasterVolume,
    play,
    pause,
    stop,
    seek,
    toggleLoop,
    exportMixData,
    importMixData,
    renderMixToFile
  } = useWebAudio();

  const {
    items: libraryItems,
    isLoading: isLoadingLibrary,
    error: libraryError,
    page: currentPage,
    totalPages,
    total: totalItems,
    setFilters,
    uploadAudio,
    rateItem,
    userRatings,
    previewAudio,
    getAudioUrl
  } = useAudioLibrary();


  // Track if we've already attempted to save the script
  const scriptSaveAttemptedRef = useRef(false);
  
  // Initialize audio on component mount and ensure script is saved
  useEffect(() => {
    const initializeAudioAndSaveScript = async () => {
      // Initialize audio
      await initializeAudio();
      
      // If no script ID exists, save the script (but only once)
      if (!scriptDbId && scriptName && !scriptSaveAttemptedRef.current) {
        console.log('No script ID found, saving script...');
        scriptSaveAttemptedRef.current = true;
        try {
          await saveScript();
        } catch (error) {
          console.error('Failed to save script:', error);
          // Reset the flag on error so we can retry
          scriptSaveAttemptedRef.current = false;
        }
      }
    };
    
    initializeAudioAndSaveScript();
  }, [initializeAudio, scriptDbId, scriptName]);
  
  // Reset the script save flag when script ID changes (new script)
  useEffect(() => {
    if (scriptDbId) {
      scriptSaveAttemptedRef.current = false;
    }
  }, [scriptDbId]);

  // Reset scroll flag after scrolling
  useEffect(() => {
    if (shouldScrollToStart) {
      // Reset the flag after a short delay to ensure the scroll happens
      const timer = setTimeout(() => {
        setShouldScrollToStart(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [shouldScrollToStart]);

  // Load main script audio when available
  useEffect(() => {
    console.log('Audio loading effect:', { scriptAudioFile, isInitialized, tracksCount: tracks.length });
    
    if (scriptAudioFile && isInitialized && mainTrackLoadedRef.current !== scriptAudioFile) {
      console.log('Loading new main track for audio:', scriptAudioFile);
      
      // Mark this audio file as loaded
      mainTrackLoadedRef.current = scriptAudioFile;
      
      addTrack({
        id: 'main-track',
        name: scriptName || 'Main Script',
        type: 'main',
        volume: 1,
        muted: false,
        startTime: 0,
        duration: 0,
        color: '#4caf50'
      });

      // Load the audio - use the API URL from config
      const audioUrl = `${config.apiUrl}${scriptAudioFile}`;
      console.log('Loading audio from URL:', audioUrl);
      loadTrackAudio('main-track', audioUrl);
    }
  }, [scriptAudioFile, isInitialized, addTrack, loadTrackAudio, scriptName]);

  // Filter library items based on selected tab, search, and sort
  useEffect(() => {
    const filters: any = { 
      audio_type: selectedLibraryTab, 
      page: 1,
      sort: sortOption
    };
    
    if (searchQuery.trim()) {
      filters.search = searchQuery.trim();
    }
    
    if (durationFilter !== 'all') {
      filters.duration_range = durationFilter;
    }
    
    // Always include my_uploads_only to ensure it's properly set/unset
    filters.my_uploads_only = myUploadsOnly;
    
    setFilters(filters);
  }, [selectedLibraryTab, searchQuery, sortOption, durationFilter, myUploadsOnly, setFilters]);

  // Memoize expensive callback functions to prevent unnecessary re-renders
  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  const handleStop = useCallback(() => {
    stop();
    setShouldScrollToStart(true);
  }, [stop]);

  const handleMasterVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const volume = parseFloat(e.target.value);
    setMasterVolume(volume);
  }, [setMasterVolume]);

  const handleTrackMute = useCallback((trackId: string, muted: boolean) => {
    updateTrack(trackId, { muted });
  }, [updateTrack]);

  const handleTrackVolumeChange = useCallback((trackId: string, volume: number) => {
    updateTrack(trackId, { volume });
  }, [updateTrack]);

  const handleAddLibraryItem = useCallback((item: any) => {
    const trackId = `library-${item.id}-${Date.now()}`;
   
    // Stop library item preview if it's playing
    if (playingAudio) {
      playingAudio.audio.pause();
      setPlayingAudio(null);
    }
    
    setShouldScrollToStart(true);
    addTrack({
      id: trackId,
      name: item.name,
      type: item.audio_type,
      audioLibraryId: item.id,
      audioFilePath: item.audio_file_path, // Store the file path for restoration
      volume: 0.7,
      muted: false,
      startTime: 0,
      duration: item.audio_duration_seconds || 0,
      color: item.audio_type === 'music' ? '#2196f3' : 
             item.audio_type === 'background' ? '#ff9800' : '#9c27b0'
    });

    const audioUrl = audioLibraryService.getAudioUrl(item);
    loadTrackAudio(trackId, audioUrl);
  }, [tracks, addTrack, loadTrackAudio, playingAudio, setPlayingAudio]);

  // Memoize main track detection to prevent unnecessary recalculations
  const hasMainTrack = useMemo(() => tracks.some(t => t.type === 'main'), [tracks]);

  // Search and sort handlers
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleSortChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortOption(e.target.value as 'popular' | 'recent' | 'rating' | 'my_favorites');
  }, []);

  const handleLibraryItemRate = useCallback(async (itemId: string, rating: 'like' | 'dislike') => {
    try {
      await rateItem(itemId, rating);
    } catch (error) {
      console.error('Failed to rate item:', error);
    }
  }, [rateItem]);

  const handlePlayAudio = useCallback(async (item: any) => {
    // Stop currently playing audio
    if (playingAudio) {
      playingAudio.audio.pause();
      setPlayingAudio(null);
      
      // If clicking the same item, just stop
      if (playingAudio.id === item.id) {
        return;
      }
    }

    try {
      const audio = await previewAudio(item);
      audio.volume = 0.5; // Set to 50% volume for preview
      
      // Set up event listeners
      const handleEnded = () => {
        setPlayingAudio(null);
        audio.removeEventListener('ended', handleEnded);
      };
      
      audio.addEventListener('ended', handleEnded);
      
      await audio.play();
      setPlayingAudio({ id: item.id, audio });
      
    } catch (error) {
      console.error('Failed to play audio:', error);
    }
  }, [playingAudio, previewAudio]);

  // Cleanup playing audio and upload progress on unmount
  useEffect(() => {
    return () => {
      if (playingAudio) {
        playingAudio.audio.pause();
      }
      if (uploadProgressIntervalRef.current) {
        clearInterval(uploadProgressIntervalRef.current);
      }
    };
  }, [playingAudio]);

  // New filter handlers
  const handleDurationFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setDurationFilter(e.target.value as 'all' | 'under_1' | '1_to_2' | '2_to_5' | '5_to_10' | 'over_10');
  }, []);

  const handleMyUploadsToggle = useCallback(() => {
    setMyUploadsOnly(!myUploadsOnly);
  }, [myUploadsOnly]);

  // Pagination handlers
  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      const filters: any = {
        audio_type: selectedLibraryTab,
        page: currentPage - 1,
        sort: sortOption,
        search: searchQuery.trim() || undefined,
        duration_range: durationFilter !== 'all' ? durationFilter : undefined,
        my_uploads_only: myUploadsOnly
      };
      setFilters(filters);
    }
  }, [currentPage, selectedLibraryTab, sortOption, searchQuery, durationFilter, myUploadsOnly, setFilters]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      const filters: any = {
        audio_type: selectedLibraryTab,
        page: currentPage + 1,
        sort: sortOption,
        search: searchQuery.trim() || undefined,
        duration_range: durationFilter !== 'all' ? durationFilter : undefined,
        my_uploads_only: myUploadsOnly
      };
      setFilters(filters);
    }
  }, [currentPage, totalPages, selectedLibraryTab, sortOption, searchQuery, durationFilter, myUploadsOnly, setFilters]);

  // Upload modal handlers
  const handleOpenUploadModal = useCallback(() => {
    setShowUploadModal(true);
    setUploadError(null);
    setUploadSuccess(null);
    setUploadForm({
      name: '',
      description: '',
      audio_type: selectedLibraryTab,
      is_private: false,
      copyright_confirmation: false,
      file: null
    });
    setAudioPreview({ audio: null, duration: 0, isPlaying: false });
    currentAudioRef.current = null;
  }, [selectedLibraryTab]);

  const handleCloseUploadModal = useCallback(() => {
    // Always allow closing, but warn if upload is in progress
    if (isUploading && uploadProgress > 0 && uploadProgress < 100) {
      const shouldClose = window.confirm('Upload is in progress. Are you sure you want to cancel?');
      if (!shouldClose) return;
      
      // Cancel upload by clearing interval
      if (uploadProgressIntervalRef.current) {
        clearInterval(uploadProgressIntervalRef.current);
        uploadProgressIntervalRef.current = null;
      }
      setIsUploading(false);
    }
    
    // Clean up audio preview
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.src = '';
      currentAudioRef.current = null;
    }
    
    setShowUploadModal(false);
    setUploadError(null);
    setUploadSuccess(null);
    setUploadProgress(0);
    setAudioPreview({ audio: null, duration: 0, isPlaying: false });
  }, [isUploading, uploadProgress, audioPreview.audio]);

  const handleUploadFormChange = useCallback((field: string, value: any) => {
    setUploadForm(prev => ({ ...prev, [field]: value }));
    setUploadError(null);
  }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      // Validate file type
      const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/webm', 'audio/m4a', 'audio/aac'];
      if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|ogg|mp4|webm|m4a|aac)$/i)) {
        setUploadError('Please select a valid audio file (MP3, WAV, OGG, MP4, M4A, AAC, or WebM)');
        return;
      }
      
      // Validate file size (50MB limit)
      if (file.size > 50 * 1024 * 1024) {
        setUploadError('File size must be less than 50MB');
        return;
      }
      
      // Clean up previous audio preview properly
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.src = '';
        if (currentAudioRef.current.src.startsWith('blob:')) {
          URL.revokeObjectURL(currentAudioRef.current.src);
        }
        currentAudioRef.current = null;
      }
      
      // Clear any previous upload error
      setUploadError(null);
      
      // Create audio element for preview and duration
      const objectUrl = URL.createObjectURL(file);
      const audio = document.createElement('audio') as HTMLAudioElement;
      
      // Set as current audio in ref
      currentAudioRef.current = audio;
      
      const handleLoadedMetadata = async () => {
        // Only proceed if this is still the current audio
        if (currentAudioRef.current !== audio) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        
        const duration = audio.duration;
        setAudioPreview({ audio, duration, isPlaying: false });
        
        // Generate waveform
        if (waveformCanvasRef.current && currentAudioRef.current === audio) {
          try {
            await generateWaveform(file, waveformCanvasRef.current);
          } catch (error) {
            console.error('Failed to generate waveform:', error);
          }
        }
      };
      
      const handleError = () => {
        // Only proceed if this is still the current audio
        if (currentAudioRef.current !== audio) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        
        console.error('Audio loading error for file:', file.name);
        setUploadError('Failed to load audio file. Please ensure it is a valid audio file.');
        setAudioPreview({ audio: null, duration: 0, isPlaying: false });
        URL.revokeObjectURL(objectUrl);
        currentAudioRef.current = null;
      };
      
      // Add event listeners
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('error', handleError);
      
      // Set source to start loading
      audio.src = objectUrl;
      
      // Auto-fill name if empty
      if (!uploadForm.name) {
        const fileName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
        setUploadForm(prev => ({ ...prev, name: fileName, file }));
      } else {
        setUploadForm(prev => ({ ...prev, file }));
      }
    }
  }, [uploadForm.name]);

  const handlePlayPreview = useCallback(() => {
    if (!audioPreview.audio) return;
    
    if (audioPreview.isPlaying) {
      audioPreview.audio.pause();
      setAudioPreview(prev => ({ ...prev, isPlaying: false }));
    } else {
      audioPreview.audio.play();
      setAudioPreview(prev => ({ ...prev, isPlaying: true }));
      
      // Set up ended listener
      audioPreview.audio.onended = () => {
        setAudioPreview(prev => ({ ...prev, isPlaying: false }));
      };
    }
  }, [audioPreview.audio, audioPreview.isPlaying]);

  const handleUploadSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!uploadForm.file) {
      setUploadError('Please select an audio file');
      return;
    }
    
    if (!uploadForm.name.trim()) {
      setUploadError('Please enter a name for the audio');
      return;
    }
    
    if (!uploadForm.copyright_confirmation) {
      setUploadError('Please confirm you have the rights to upload this audio');
      return;
    }
    
    setIsUploading(true);
    setUploadError(null);
    setUploadProgress(0);
    
    try {
      // Use the new upload processor
      const result = await processAndUploadAudio(
        uploadForm.file,
        (stage, progress) => {
          // Map stages to overall progress
          const stageWeights: Record<string, { start: number; end: number }> = {
            'analyzing': { start: 0, end: 10 },
            'converting': { start: 10, end: 30 },
            'waveform': { start: 30, end: 40 },
            'preparing': { start: 40, end: 50 },
            'uploading_audio': { start: 50, end: 80 },
            'uploading_waveform': { start: 80, end: 90 }
          };
          
          const weight = stageWeights[stage];
          if (weight) {
            const stageProgress = weight.start + (progress / 100) * (weight.end - weight.start);
            setUploadProgress(Math.round(stageProgress));
          }
        }
      );
      
      // Save metadata to database
      setUploadProgress(95);
      await saveAudioMetadata({
        name: uploadForm.name.trim(),
        description: uploadForm.description.trim() || undefined,
        audio_type: uploadForm.audio_type,
        is_private: uploadForm.is_private,
        s3_audio_key: result.s3AudioKey!,
        s3_waveform_key: result.s3WaveformKey!,
        duration: result.metadata.duration,
        copyright_confirmed: uploadForm.copyright_confirmation
      });
      
      setUploadProgress(100);
      setUploadSuccess('Audio uploaded successfully!');
      
      // Refresh audio library
      setFilters({ audio_type: uploadForm.audio_type, page: 1, sort: 'recent' });
      
      // Auto-close modal after success
      setTimeout(() => {
        handleCloseUploadModal();
      }, 1500);
      
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to upload audio');
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  }, [uploadForm, handleCloseUploadModal, setFilters]);

  const handleBrainwaveGenerate = useCallback((settings: BrainwaveSettings) => {
    if (!audioContext) return;

    const trackId = `brainwave-${Date.now()}`;
    const duration = settings.controlPoints[settings.controlPoints.length - 1]?.time || 300;
    // Use previewVolume if provided, otherwise fall back to control point amplitude
    const volume = settings.previewVolume || settings.controlPoints[0]?.amplitude || 0.3;
    
    console.log('Adding brainwave track:', {
      trackId,
      settings,
      duration,
      volume
    });
    
    addTrack({
      id: trackId,
      name: `${settings.preset || 'Custom'} Brainwave`,
      type: 'brainwave',
      volume: volume,
      muted: false,
      startTime: 0,
      duration: duration,
      color: '#e91e63',
      brainwaveSettings: settings
    });
  }, [audioContext, addTrack]);

  const handleNoiseGenerate = useCallback((settings: NoiseSettings, presetName: string) => {
    if (!audioContext) return;

    const trackId = `noise-${Date.now()}`;
    
    console.log('Adding noise track:', {
      trackId,
      settings,
      presetName
    });
    
    addTrack({
      id: trackId,
      name: presetName,
      type: 'noise',
      volume: settings.volume,
      muted: false,
      startTime: 0,
      duration: settings.duration,
      color: '#00bcd4',
      noiseSettings: settings
    });
  }, [audioContext, addTrack]);

  const handleDuplicateTrack = useCallback((trackId: string) => {
    const track = tracks.find(t => t.id === trackId);
    if (!track) return;

    // For brainwave tracks, we don't need an audio buffer
    if (track.type === 'brainwave' && !track.brainwaveSettings) return;
    // For noise tracks, we don't need an audio buffer
    if (track.type === 'noise' && !track.noiseSettings) return;
    // For regular tracks, we need an audio buffer
    if (track.type !== 'brainwave' && track.type !== 'noise' && !track.audioBuffer) return;

    const newTrackId = `${track.type}-${Date.now()}`;
    const duplicatedTrack: any = {
      id: newTrackId,
      name: `${track.name} (Copy)`,
      type: track.type,
      audioFilePath: track.audioFilePath,
      audioLibraryId: track.audioLibraryId,
      volume: track.volume,
      muted: track.muted,
      startTime: (track.startTime || 0) + (track.trimmedDuration || track.duration), // Place after original with no gap
      duration: track.duration,
      color: track.color,
      trimmedDuration: track.trimmedDuration,
      fadeIn: track.fadeIn,
      fadeOut: track.fadeOut
    };

    // Copy brainwave settings if it's a brainwave track
    if (track.type === 'brainwave' && track.brainwaveSettings) {
      duplicatedTrack.brainwaveSettings = { ...track.brainwaveSettings };
    }
    
    // Copy noise settings if it's a noise track
    if (track.type === 'noise' && track.noiseSettings) {
      duplicatedTrack.noiseSettings = { ...track.noiseSettings };
    }

    addTrack(duplicatedTrack);
    
    // Load audio buffer for regular tracks
    if (track.type !== 'brainwave' && track.type !== 'noise' && track.audioBuffer) {
      loadTrackAudioBuffer(newTrackId, track.audioBuffer);
    }
  }, [tracks, addTrack, loadTrackAudioBuffer]);

  const handleLoopTrack = useCallback((trackId: string) => {
    const track = tracks.find(t => t.id === trackId);
    if (!track) return;
    
    // For brainwave tracks, we don't need an audio buffer
    if (track.type === 'brainwave' && !track.brainwaveSettings) return;
    // For noise tracks, we don't need an audio buffer
    if (track.type === 'noise' && !track.noiseSettings) return;
    // For regular tracks, we need an audio buffer
    if (track.type !== 'brainwave' && track.type !== 'noise' && !track.audioBuffer) return;

    // Find the longest track duration, considering trimmed durations
    const longestDuration = Math.max(...tracks.map(t => 
      t.isLoaded ? (t.startTime || 0) + (t.trimmedDuration || t.duration) : 0
    ));

    // Calculate the actual end time of this track (considering trimmed duration)
    const effectiveDuration = track.trimmedDuration || track.duration;
    const trackEndTime = (track.startTime || 0) + effectiveDuration;

    if (longestDuration <= trackEndTime) {
      console.warn('Track is already the longest or extends to the end');
      return;
    }

    // Calculate how many times to loop
    let currentPosition = trackEndTime;
    let loopCount = 0;

    while (currentPosition < longestDuration) {
      loopCount++;
      const newTrackId = `${track.type}-loop-${Date.now()}-${loopCount}`;
      const gap = 0.0; // No gap between loops
      
      const loopedTrack: any = {
        id: newTrackId,
        name: `${track.name} (Loop ${loopCount})`,
        type: track.type,
        audioFilePath: track.audioFilePath,
        audioLibraryId: track.audioLibraryId,
        volume: track.volume,
        muted: track.muted,
        startTime: currentPosition + gap,
        duration: track.duration, // Always use full duration for the audio buffer
        trimmedDuration: track.trimmedDuration, // Copy the trimmed duration if it exists
        color: track.color,
        fadeIn: track.fadeIn,
        fadeOut: track.fadeOut
      };

      // Copy brainwave settings if it's a brainwave track
      if (track.type === 'brainwave' && track.brainwaveSettings) {
        loopedTrack.brainwaveSettings = { ...track.brainwaveSettings };
      }
      
      // Copy noise settings if it's a noise track
      if (track.type === 'noise' && track.noiseSettings) {
        loopedTrack.noiseSettings = { ...track.noiseSettings };
      }

      // Make sure the last loop doesn't exceed the longest duration
      const remainingTime = longestDuration - (currentPosition + gap);
      if (track.trimmedDuration) {
        loopedTrack.trimmedDuration = Math.min(track.trimmedDuration, remainingTime);
      } else if (remainingTime < track.duration) {
        loopedTrack.trimmedDuration = remainingTime;
      }

      addTrack(loopedTrack);
      
      // Load audio buffer for regular tracks
      if (track.type !== 'brainwave' && track.type !== 'noise' && track.audioBuffer) {
        loadTrackAudioBuffer(newTrackId, track.audioBuffer);
      }
      
      currentPosition += effectiveDuration + gap;
    }
  }, [tracks, addTrack, loadTrackAudioBuffer]);

  // Track the serializable state for auto-save change detection AND for script save
  const currentMixState = useMemo(() => {
    if (tracks.length === 0) return null;
    
    const nonMainTracks = tracks.filter(track => track.id !== 'main-track');
    if (nonMainTracks.length === 0) return null;
    
    // Only include serializable properties that affect the saved mix
    const totalDuration = Math.max(...tracks.map(track => (track.startTime || 0) + (track.trimmedDuration || track.duration || 0)));
    
    return {
      masterVolume,
      totalDuration,
      tracks: tracks.map(track => ({
        id: track.id,
        name: track.name,
        type: track.type,
        audioLibraryId: track.audioLibraryId,
        audioFilePath: track.audioFilePath,
        volume: track.volume,
        muted: track.muted,
        startTime: track.startTime,
        duration: track.duration,
        trimmedDuration: track.trimmedDuration,
        color: track.color,
        fadeIn: track.fadeIn,
        fadeOut: track.fadeOut,
        brainwaveSettings: track.brainwaveSettings
      }))
    };
  }, [tracks, masterVolume]); // Removed autoSaveEnabled from deps since we always want to compute state

  // Update context with current mix state for script save
  useEffect(() => {
    setCurrentMixState(currentMixState);
  }, [currentMixState, setCurrentMixState]);

  // Store the last saved state to compare for changes
  const lastSavedStateRef = useRef<typeof currentMixState>(null);

  // Memoize auto-save callback to prevent recreation
  const handleAutoSave = useCallback(async () => {
    try {
      console.log('Auto-saving audio mix...');
      const mixData = exportMixData();
      // Preserve existing createdAt timestamp if we have audioMixData
      if (audioMixData?.createdAt) {
        mixData.createdAt = audioMixData.createdAt;
      }
      await saveAudioMix(mixData);
      setLastSavedTime(new Date());
      setSaveError(null);
      
      // Update the last saved state reference
      lastSavedStateRef.current = currentMixState;
    } catch (error) {
      console.error('Auto-save failed:', error);
      setSaveError('Auto-save failed');
    }
  }, [exportMixData, saveAudioMix, currentMixState, audioMixData]);

  // Auto-save functionality with change detection
  useEffect(() => {
    // Don't auto-save if importing, disabled, or hasn't loaded saved mix yet
    if (!autoSaveEnabled || isImporting || hasLoadedSavedMix.current === false) return;
    
    // No saveable state
    if (!currentMixState) return;
    
    // Check if state has actually changed since last save
    const hasChanged = JSON.stringify(currentMixState) !== JSON.stringify(lastSavedStateRef.current);
    if (!hasChanged) {
      console.log('Auto-save: No changes detected, skipping save');
      return;
    }

    console.log('Auto-save: Changes detected, scheduling save...');

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout for auto-save (2 seconds after last change)
    autoSaveTimeoutRef.current = setTimeout(handleAutoSave, 2000);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [currentMixState, autoSaveEnabled, isImporting, handleAutoSave]);

  // Load saved mix data when it becomes available
  const hasLoadedSavedMix = useRef(false);
  const hasCheckedForSavedMix = useRef(false);
  const lastLoadedMixId = useRef<string | null>(null);
  useEffect(() => {
    const loadSavedMix = async () => {
      if (!isInitialized) return;
      
      // If we've already loaded the saved mix, don't load again
      if (hasLoadedSavedMix.current) return;
      
      // If there's no saved audio mix data yet, just mark that we've checked
      if (!audioMixData) {
        // Only enable auto-save if we've checked at least once and confirmed no data
        if (hasCheckedForSavedMix.current) {
          setAutoSaveEnabled(true);
        }
        hasCheckedForSavedMix.current = true;
        return;
      }
      
      // Check if this is a different mix (different createdAt timestamp)
      const currentMixId = audioMixData?.createdAt || null;
      if (currentMixId === lastLoadedMixId.current) {
        return;
      }
      
      // If this is a different mix ID, reset the loaded flag so we can load the new one
      if (currentMixId !== lastLoadedMixId.current) {
        hasLoadedSavedMix.current = false;
      }

      try {
        console.log('Loading saved audio mix...');
        console.log('Audio mix data:', audioMixData);
        hasLoadedSavedMix.current = true;
        lastLoadedMixId.current = currentMixId;
        setIsImporting(true);
        
        // Small delay to ensure audio context is ready
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Pass the script audio URL for main track restoration
        const scriptAudioUrl = scriptAudioFile ? `${config.apiUrl}${scriptAudioFile}` : undefined;
        await importMixData(audioMixData, scriptAudioUrl, config.apiUrl);
        setLastSavedTime(new Date(audioMixData.updatedAt));
        console.log('Loaded saved audio mix successfully');
        
        // Initialize the last saved state to prevent immediate auto-save after loading
        setTimeout(() => {
          if (currentMixState) {
            lastSavedStateRef.current = currentMixState;
          }
        }, 200); // Small delay to ensure currentMixState is updated
        
        // Enable auto-save after successful initial load
        setAutoSaveEnabled(true);
      } catch (error) {
        hasLoadedSavedMix.current = false; // Reset on error
        lastLoadedMixId.current = null; // Reset mix ID on error
        console.error('Failed to load saved mix:', error);
      } finally {
        setIsImporting(false);
      }
    };

    loadSavedMix();
  }, [isInitialized, audioMixData, importMixData, scriptAudioFile]);

  // Manual save handler
  const handleManualSave = useCallback(async () => {
    if (isSaving) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      const mixData = exportMixData();
      // Preserve existing createdAt timestamp if we have audioMixData
      if (audioMixData?.createdAt) {
        mixData.createdAt = audioMixData.createdAt;
      }
      await saveAudioMix(mixData);
      setLastSavedTime(new Date());
      
      // Update the last saved state reference to prevent auto-save trigger
      lastSavedStateRef.current = currentMixState;
    } catch (error) {
      console.error('Manual save failed:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  }, [exportMixData, saveAudioMix, isSaving, currentMixState, audioMixData]);

  // Clear saved mix handler
  const handleClearSavedMix = useCallback(async () => {
    try {
      // Reset the audio state
      tracks.forEach(track => {
        if (track.id !== 'main-track') {
          removeTrack(track.id);
        }
      });
      setMasterVolume(1.0);
      setLastSavedTime(null);
      setSaveError(null);
      
      // Clear the last saved state reference
      lastSavedStateRef.current = null;
    } catch (error) {
      console.error('Failed to clear mix:', error);
    }
  }, [tracks, removeTrack, setMasterVolume]);

  // Finalize audio mix handler
  const handleFinalizeAudioMix = useCallback(async () => {
    if (!scriptDbId || isFinalizing) {
      console.warn('Cannot finalize: missing script ID or already finalizing');
      return;
    }

    // Check if there are any audio tracks, brainwave tracks, or noise tracks
    const playableTracks = tracks.filter(track => 
      (track.isLoaded && track.audioBuffer) || 
      (track.type === 'brainwave' && track.brainwaveSettings) ||
      (track.type === 'noise' && track.noiseSettings)
    );
    
    if (playableTracks.length === 0) {
      console.warn('Cannot finalize: no audio, brainwave, or noise tracks available');
      return;
    }

    setIsFinalizing(true);
    setFinalizeError(null);
    setFinalizeSteps({
      rendering: { progress: 0, active: true, complete: false, indeterminate: true },
      compressing: { progress: 0, active: false, complete: false, indeterminate: false },
      uploading: { progress: 0, active: false, complete: false, indeterminate: false },
      metadata: { progress: 0, active: false, complete: false, indeterminate: false }
    });

    try {
      console.log('Starting audio mix finalization...');
      
      // Save the audio mix first
      console.log('Saving audio mix before finalization...');
      await handleManualSave();
      
      // Step 1: Render and compress the mix
      const audioBlob = await renderMixToFile((stage, progress) => {
        if (stage === 'rendering') {
          if (progress === 0) {
            // Rendering started - keep as indeterminate
            console.log('Rendering audio buffer...');
          } else if (progress === 100) {
            // Rendering complete, start compression
            console.log('Rendering complete, starting compression...');
            setFinalizeSteps(prev => ({
              ...prev,
              rendering: { progress: 100, active: false, complete: true, indeterminate: false },
              compressing: { progress: 0, active: true, complete: false, indeterminate: false }
            }));
          }
        } else if (stage === 'encoding') {
          // Encoding/Compressing phase - we have real progress here
          setFinalizeSteps(prev => ({
            ...prev,
            compressing: { ...prev.compressing, progress: Math.round(progress) }
          }));
        }
      });
      
      // Mark compression as complete
      setFinalizeSteps(prev => ({
        ...prev,
        compressing: { progress: 100, active: false, complete: true, indeterminate: false },
        uploading: { progress: 0, active: true, complete: false, indeterminate: false }
      }));

      // Step 2: Determine file extension based on blob type and get presigned URL
      console.log('Determining audio format from blob...');
      let fileExtension = 'mp4'; // Default fallback
      
      if (audioBlob.type.includes('audio/mp4') || audioBlob.type.includes('audio/aac')) {
        fileExtension = 'mp4';
      } else if (audioBlob.type.includes('audio/mpeg') || audioBlob.type.includes('audio/mp3')) {
        fileExtension = 'mp3';
      }
      
      console.log(`Audio blob type: ${audioBlob.type}, using extension: ${fileExtension}`);
      
      // Get presigned URL from backend
      const presignedResponse = await fetch(`${config.apiUrl}/generate-s3-presigned-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          scriptId: scriptDbId,
          fileExtension: fileExtension
        })
      });

      if (!presignedResponse.ok) {
        throw new Error('Failed to get S3 presigned URL');
      }

      const { presignedUrl, key } = await presignedResponse.json();
      
      console.log('Uploading to S3...', { key });
      
      // Upload directly to S3 with progress tracking
      const xhr = new XMLHttpRequest();
      
      const uploadPromise = new Promise<void>((resolve, reject) => {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            setFinalizeSteps(prev => ({
              ...prev,
              uploading: { ...prev.uploading, progress }
            }));
          }
        };
        
        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject(new Error(`S3 upload failed with status: ${xhr.status}`));
          }
        };
        
        xhr.onerror = () => {
          reject(new Error('S3 upload failed'));
        };
        
        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', 'audio/mp4');
        xhr.send(audioBlob);
      });
      
      await uploadPromise;
      console.log('S3 upload successful');
      
      // Step 3: Send filename and duration to backend to save in database
      console.log('Saving audio metadata to database...');
      
      // Calculate duration from the audio blob (we already have this from the render process)
      const audioDuration = audioBlob.size ? Math.round(duration) : 0;
      
      const saveMetadataResponse = await fetch(`${config.apiUrl}/scripts/${scriptDbId}/save-audio-metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          s3Key: key,
          duration: audioDuration
        })
      });

      if (!saveMetadataResponse.ok) {
        throw new Error('Failed to save audio metadata');
      }

      const metadataResult = await saveMetadataResponse.json();
      console.log('Audio metadata saved successfully:', metadataResult);

      // Update context with final audio file path (S3 key)
      setScriptFinalAudioFile(key);

      // Mark upload as complete
      setFinalizeSteps(prev => ({
        ...prev,
        uploading: { progress: 100, active: false, complete: true, indeterminate: false },
        metadata: { progress: 0, active: true, complete: false, indeterminate: false }
      }));
      
      // Step 4: Generate script metadata for publish page
      console.log('Generating script metadata...');
      
      // Check if this is an unguided meditation (only selfguided part selected)
      const isUnguided = selectedParts.length === 1 && selectedParts.includes('selfguided');
      
      // Show progress for metadata generation
      setFinalizeSteps(prev => ({
        ...prev,
        metadata: { ...prev.metadata, progress: 50, indeterminate: false }
      }));
      
      if (isUnguided) {
        // For unguided meditations, analyze audio tracks
        console.log('Generating unguided metadata from audio tracks...');
        await generateUnguidedMetadata(tracks);
      } else {
        // For guided meditations, analyze script text
        console.log('Generating guided metadata from script text...');
        await generateScriptMetadata();
      }
      
      // Mark all steps as complete
      setFinalizeSteps({
        rendering: { progress: 100, active: false, complete: true, indeterminate: false },
        compressing: { progress: 100, active: false, complete: true, indeterminate: false },
        uploading: { progress: 100, active: false, complete: true, indeterminate: false },
        metadata: { progress: 100, active: false, complete: true, indeterminate: false }
      });

      // Step 5: Navigate to Publish page after a brief delay
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });//scroll to top of page
        const basePath = location.pathname.includes('/edit/') 
          ? `/edit/${scriptDbId}`
          : '/create';
        navigate(`${basePath}/publish`);
      }, 1000);

    } catch (error) {
      console.error('Failed to finalize audio mix:', error);
      setFinalizeError(error instanceof Error ? error.message : 'Failed to finalize audio mix');
      setFinalizeSteps({
        rendering: { progress: 0, active: false, complete: false, indeterminate: false },
        compressing: { progress: 0, active: false, complete: false, indeterminate: false },
        uploading: { progress: 0, active: false, complete: false, indeterminate: false },
        metadata: { progress: 0, active: false, complete: false, indeterminate: false }
      });
    } finally {
      setIsFinalizing(false);
    }
  }, [scriptDbId, tracks, renderMixToFile, navigate, location.pathname, isFinalizing, setScriptFinalAudioFile, generateScriptMetadata, generateUnguidedMetadata, selectedParts]);

  // Determine if finalize button should be enabled
  const canFinalize = useMemo(() => {
    // Check for regular loaded tracks, brainwave tracks, or noise tracks
    const hasPlayableTracks = tracks.some(track => 
      (track.isLoaded && track.audioBuffer) || 
      (track.type === 'brainwave' && track.brainwaveSettings) ||
      (track.type === 'noise' && track.noiseSettings)
    );
    
    return !isFinalizing && 
           scriptDbId && 
           hasPlayableTracks &&
           !isImporting;
  }, [isFinalizing, scriptDbId, tracks, isImporting]);

  return (
    <Container>
      <Header>
        <Title>Audio Enhancement</Title>
        <Description>
          Layer background music, ambient sounds, and effects to create the perfect meditation experience.
        </Description>
      </Header>

      <MainContent>
        <TimelineSection>
          <SectionTitle>
            <Icon icon={FaMusic} />
            Audio Timeline
          </SectionTitle>

          <SaveControls>
            <SaveButton
              onClick={handleManualSave}
              disabled={isSaving || tracks.length === 0}
            >
              <Icon icon={FaSave} />
              {isSaving ? 'Saving...' : 'Save Mix'}
            </SaveButton>
            
            <SaveButton
              $variant="clear"
              onClick={handleClearSavedMix}
              disabled={tracks.length === 0 || (tracks.length === 1 && tracks[0].type === 'main')} // Allow clearing any tracks
            >
              <Icon icon={FaTrash} />
              Clear Mix
            </SaveButton>

            <SaveStatus>
              {saveError && (
                <SaveStatusText $type="error">
                  {saveError}
                </SaveStatusText>
              )}
              {lastSavedTime && !saveError && (
                <SaveStatusText $type="success">
                  Last saved: {lastSavedTime.toLocaleTimeString()}
                </SaveStatusText>
              )}
              {!lastSavedTime && !saveError && tracks.length > 0 && (
                <SaveStatusText $type="info">
                  Mix not saved
                </SaveStatusText>
              )}
            </SaveStatus>

            <AutoSaveToggle>
              <ToggleLabel>
                <ToggleInput
                  type="checkbox"
                  checked={autoSaveEnabled}
                  onChange={(e) => setAutoSaveEnabled(e.target.checked)}
                />
                Auto-save
              </ToggleLabel>
            </AutoSaveToggle>
          </SaveControls>
          
          <PlaybackControls>
            <ControlButton
              $variant="primary"
              onClick={handlePlayPause}
              disabled={!isInitialized || tracks.length === 0}
            >
              <Icon icon={isPlaying ? FaPause : FaPlay} />
              {isPlaying ? 'Pause' : 'Play'}
            </ControlButton>

            <ControlButton
              $variant={isLooping ? 'accent' : 'secondary'}
              onClick={toggleLoop}
              disabled={!isInitialized || tracks.length === 0}
              title={isLooping ? 'Disable loop' : 'Enable loop'}
            >
              <Icon icon={FaRedoAlt} />
              Loop
            </ControlButton>

            <ControlButton
              onClick={handleStop}
              disabled={!isInitialized || tracks.length === 0}
            >
              <Icon icon={FaStop} />
              Stop
            </ControlButton>

            <TimeDisplay>
              {formatTime(currentTime)} / {formatTime(duration)}
            </TimeDisplay>

            <VolumeControl>
              <Icon icon={FaVolumeUp} />
              <VolumeSlider
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={masterVolume}
                onChange={handleMasterVolumeChange}
              />
              <span style={{ color: '#fff', fontSize: '12px' }}>
                {Math.round(masterVolume * 100)}%
              </span>
            </VolumeControl>
          </PlaybackControls>

          

          <AudioTimeline
            tracks={tracks}
            currentTime={currentTime}
            duration={duration}
            isPlaying={isPlaying}
            onUpdateTrack={updateTrack}
            onRemoveTrack={removeTrack}
            onDuplicateTrack={handleDuplicateTrack}
            onLoopTrack={handleLoopTrack}
            onSeek={seek}
            scrollToStart={shouldScrollToStart}
          />

          {/* Finalize Audio Section */}
          <FinalizeSection>
            {isFinalizing && (
              <FinalizeProgress>
                <FinalizeStepContainer>
                  <FinalizeStepLabel $active={finalizeSteps.rendering.active} $complete={finalizeSteps.rendering.complete}>
                    <FinalizeStepName>
                      <FinalizeStepIcon $complete={finalizeSteps.rendering.complete}>
                        {finalizeSteps.rendering.complete ? '✓' : '○'}
                      </FinalizeStepIcon>
                      Rendering audio mix
                    </FinalizeStepName>
                    <FinalizeStepProgress>
                      {finalizeSteps.rendering.indeterminate ? 'Processing...' : `${Math.round(finalizeSteps.rendering.progress)}%`}
                    </FinalizeStepProgress>
                  </FinalizeStepLabel>
                  <FinalizeProgressBar>
                    <FinalizeProgressFill 
                      $progress={finalizeSteps.rendering.progress} 
                      $active={finalizeSteps.rendering.active}
                      $complete={finalizeSteps.rendering.complete}
                      $indeterminate={finalizeSteps.rendering.indeterminate}
                    />
                  </FinalizeProgressBar>
                </FinalizeStepContainer>
                
                <FinalizeStepContainer>
                  <FinalizeStepLabel $active={finalizeSteps.compressing.active} $complete={finalizeSteps.compressing.complete}>
                    <FinalizeStepName>
                      <FinalizeStepIcon $complete={finalizeSteps.compressing.complete}>
                        {finalizeSteps.compressing.complete ? '✓' : '○'}
                      </FinalizeStepIcon>
                      Compressing audio
                    </FinalizeStepName>
                    <FinalizeStepProgress>{Math.round(finalizeSteps.compressing.progress)}%</FinalizeStepProgress>
                  </FinalizeStepLabel>
                  <FinalizeProgressBar>
                    <FinalizeProgressFill 
                      $progress={finalizeSteps.compressing.progress} 
                      $active={finalizeSteps.compressing.active}
                      $complete={finalizeSteps.compressing.complete}
                    />
                  </FinalizeProgressBar>
                </FinalizeStepContainer>
                
                <FinalizeStepContainer>
                  <FinalizeStepLabel $active={finalizeSteps.uploading.active} $complete={finalizeSteps.uploading.complete}>
                    <FinalizeStepName>
                      <FinalizeStepIcon $complete={finalizeSteps.uploading.complete}>
                        {finalizeSteps.uploading.complete ? '✓' : '○'}
                      </FinalizeStepIcon>
                      Uploading audio
                    </FinalizeStepName>
                    <FinalizeStepProgress>{Math.round(finalizeSteps.uploading.progress)}%</FinalizeStepProgress>
                  </FinalizeStepLabel>
                  <FinalizeProgressBar>
                    <FinalizeProgressFill 
                      $progress={finalizeSteps.uploading.progress} 
                      $active={finalizeSteps.uploading.active}
                      $complete={finalizeSteps.uploading.complete}
                    />
                  </FinalizeProgressBar>
                </FinalizeStepContainer>
                
                <FinalizeStepContainer>
                  <FinalizeStepLabel $active={finalizeSteps.metadata.active} $complete={finalizeSteps.metadata.complete}>
                    <FinalizeStepName>
                      <FinalizeStepIcon $complete={finalizeSteps.metadata.complete}>
                        {finalizeSteps.metadata.complete ? '✓' : '○'}
                      </FinalizeStepIcon>
                      Generating metadata
                    </FinalizeStepName>
                    <FinalizeStepProgress>{Math.round(finalizeSteps.metadata.progress)}%</FinalizeStepProgress>
                  </FinalizeStepLabel>
                  <FinalizeProgressBar>
                    <FinalizeProgressFill 
                      $progress={finalizeSteps.metadata.progress} 
                      $active={finalizeSteps.metadata.active}
                      $complete={finalizeSteps.metadata.complete}
                    />
                  </FinalizeProgressBar>
                </FinalizeStepContainer>
              </FinalizeProgress>
            )}
            
            {finalizeError && (
              <FinalizeProgress>
                <div style={{ color: '#f44336', fontSize: '12px', textAlign: 'center' }}>
                  {finalizeError}
                </div>
              </FinalizeProgress>
            )}

            <FinalizeButton
              onClick={handleFinalizeAudioMix}
              $disabled={!canFinalize}
              $loading={isFinalizing}
              title={!canFinalize ? 'Add audio tracks to finalize' : 'Combine all tracks into final audio file'}
            >
              <Icon icon={FaRocket} />
              {isFinalizing ? 'Finalizing...' : 'Finalize Audio'}
            </FinalizeButton>
          </FinalizeSection>
        </TimelineSection>

        <LibrarySection>
          <LibraryHeader>
            <LibraryHeaderTop>
              <SectionTitle>
                <Icon icon={FaTree} />
                Audio Library
              </SectionTitle>
              <UploadButton onClick={handleOpenUploadModal}>
                <Icon icon={FaUpload} />
                Upload Audio
              </UploadButton>
            </LibraryHeaderTop>
            
            <LibraryControls>
              <SearchContainer>
                <SearchIcon>
                  <Icon icon={FaSearch} />
                </SearchIcon>
                <SearchInput
                  type="text"
                  placeholder="Search audio library..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </SearchContainer>
              
              <SortContainer>
                <SortLabel>Sort:</SortLabel>
                <SortSelect value={sortOption} onChange={handleSortChange}>
                  <option value="popular">Popular</option>
                  <option value="my_favorites">My Favorites</option>
                  <option value="rating">Everyone's Favorites</option>
                  <option value="recent">New</option>
                </SortSelect>
              </SortContainer>
              
              <DurationContainer>
                <DurationLabel>Length:</DurationLabel>
                <DurationSelect value={durationFilter} onChange={handleDurationFilterChange}>
                  <option value="all">All</option>
                  <option value="under_1">&lt;1 min</option>
                  <option value="1_to_2">1-2 min</option>
                  <option value="2_to_5">2-5 min</option>
                  <option value="5_to_10">5-10 min</option>
                  <option value="over_10">10+ min</option>
                </DurationSelect>
              </DurationContainer>
              
              <MyUploadsToggle active={myUploadsOnly} onClick={handleMyUploadsToggle}>
                My Uploads
              </MyUploadsToggle>
            </LibraryControls>
          </LibraryHeader>

          <LibraryTabs>
            <LibraryTab
              $active={selectedLibraryTab === 'music'}
              onClick={() => setSelectedLibraryTab('music')}
            >
              <Icon icon={FaMusic} />
              Music
            </LibraryTab>
            <LibraryTab
              $active={selectedLibraryTab === 'background'}
              onClick={() => setSelectedLibraryTab('background')}
            >
              <Icon icon={FaTree} />
              Ambient
            </LibraryTab>
            <LibraryTab
              $active={selectedLibraryTab === 'sound'}
              onClick={() => setSelectedLibraryTab('sound')}
            >
              <Icon icon={FaBell} />
              Effects
            </LibraryTab>
          </LibraryTabs>

          <LibraryItemsList>
            {isLoadingLibrary ? (
              <EmptyState>
                <EmptyStateText>Loading audio library...</EmptyStateText>
              </EmptyState>
            ) : libraryError ? (
              <EmptyState>
                <EmptyStateText>Unable to load audio library. Please try again later.</EmptyStateText>
              </EmptyState>
            ) : libraryItems.length === 0 ? (
              <EmptyState>
                <EmptyStateText>No {selectedLibraryTab} found</EmptyStateText>
              </EmptyState>
            ) : (
              libraryItems.map(item => {
                const isPlaying = playingAudio?.id === item.id;
                const userRating = userRatings[item.id];
                const waveformUrl = audioLibraryService.getWaveformUrl(item);
                
                return (
                  <LibraryItem key={item.id}>
                    {waveformUrl && (
                      <WaveformOverlay style={{ backgroundImage: `url('${waveformUrl}')` }} />
                    )}
                    <LibraryItemContent>
                      <LibraryItemTitleRow>
                        <LibraryItemName>{item.name}</LibraryItemName>
                        {item.is_private && item.user_id === user?.id && (
                          <PrivateTag>private</PrivateTag>
                        )}
                      </LibraryItemTitleRow>
                      
                      {item.description && (
                        <LibraryItemDescription>{item.description}</LibraryItemDescription>
                      )}
                      
                      <LibraryItemActions>
                        <LibraryItemInfo>
                          <span>{item.audio_duration_seconds ? formatTime(item.audio_duration_seconds) : 'Unknown'}</span>
                        </LibraryItemInfo>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <PlayButton 
                            $playing={isPlaying}
                            onClick={() => handlePlayAudio(item)}
                          >
                            <Icon icon={isPlaying ? FaPause : FaPlay} />
                            {isPlaying ? 'Stop' : 'Play'}
                          </PlayButton>
                          
                          <RatingButtons>
                            <RatingButton
                              $active={userRating === 'like'}
                              $type="like"
                              onClick={() => handleLibraryItemRate(item.id, 'like')}
                            >
                              <Icon icon={FaThumbsUp} />
                              <span>{item.thumbs_up}</span>
                            </RatingButton>
                            
                            <RatingButton
                              $active={userRating === 'dislike'}
                              $type="dislike"
                              onClick={() => handleLibraryItemRate(item.id, 'dislike')}
                            >
                              <Icon icon={FaThumbsDown} />
                              <span>{item.thumbs_down}</span>
                            </RatingButton>
                          </RatingButtons>
                          
                          <AddTrackButton as="span" role="button" onClick={() => handleAddLibraryItem(item)}>
                            <Icon icon={FaPlus} />
                          </AddTrackButton>
                        </div>
                      </LibraryItemActions>
                    </LibraryItemContent>
                  </LibraryItem>
                );
              })
            )}
          </LibraryItemsList>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <PaginationContainer>
              <PaginationButton 
                disabled={currentPage === 1}
                onClick={handlePreviousPage}
              >
                Previous
              </PaginationButton>
              
              <PaginationInfo>
                Page {currentPage} of {totalPages} ({totalItems} items)
              </PaginationInfo>
              
              <PaginationButton 
                disabled={currentPage === totalPages}
                onClick={handleNextPage}
              >
                Next
              </PaginationButton>
            </PaginationContainer>
          )}
        </LibrarySection>

        {/* Brainwave Generator */}
          <BrainwaveGeneratorV2 
            audioContext={audioContext} 
            onAddTrack={handleBrainwaveGenerate}
            timelineDuration={duration}
          />
        
        {/* White Noise Generator */}
          <WhiteNoiseGenerator
            audioContext={audioContext}
            onAddTrack={handleNoiseGenerate}
            timelineDuration={duration}
          />
        
        <HelpButton
        title="Create Your Meditation - Audio"
        content={helpContent}
        position="fixed-bottom-right"
        />
      </MainContent>

      {/* Upload Modal */}
      {showUploadModal && (
        <ModalOverlay onClick={handleCloseUploadModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Upload Audio to Library</ModalTitle>
              <CloseButton onClick={handleCloseUploadModal}>
                <Icon icon={FaTimes} />
              </CloseButton>
            </ModalHeader>

            <form onSubmit={handleUploadSubmit}>
              {uploadError && <ErrorMessage>{uploadError}</ErrorMessage>}
              {uploadSuccess && <SuccessMessage>{uploadSuccess}</SuccessMessage>}
              
              {isUploading && (
                <UploadProgress>
                  <ProgressBar>
                    <ProgressFill $progress={uploadProgress} />
                  </ProgressBar>
                  <ProgressText>
                    {uploadProgress < 10 ? 'Analyzing audio...' :
                     uploadProgress < 30 ? 'Converting format...' :
                     uploadProgress < 40 ? 'Generating waveform...' :
                     uploadProgress < 50 ? 'Preparing upload...' :
                     uploadProgress < 80 ? 'Uploading audio...' :
                     uploadProgress < 90 ? 'Uploading waveform...' :
                     uploadProgress < 100 ? 'Saving metadata...' :
                     'Complete!'} {Math.round(uploadProgress)}%
                  </ProgressText>
                </UploadProgress>
              )}

              <FormField>
                <Label htmlFor="audioFile">Audio File *</Label>
                <FileInput
                  id="audioFile"
                  type="file"
                  accept="audio/*"
                  onChange={handleFileChange}
                  required
                  disabled={isUploading}
                />
              </FormField>

              {uploadForm.file && (
                <>
                  <WaveformCanvas ref={waveformCanvasRef} />
                  <AudioPreviewControls>
                    <PreviewButton
                      type="button"
                      onClick={handlePlayPreview}
                      disabled={!audioPreview.audio || isUploading}
                    >
                      <Icon icon={audioPreview.isPlaying ? FaPause : FaPlay} />
                      {audioPreview.isPlaying ? 'Stop' : 'Play'}
                    </PreviewButton>
                    {audioPreview.duration > 0 && (
                      <AudioDuration>
                        Duration: {formatTime(audioPreview.duration)}
                      </AudioDuration>
                    )}
                  </AudioPreviewControls>
                </>
              )}

              <FormField>
                <Label htmlFor="audioName">Name *</Label>
                <Input
                  id="audioName"
                  type="text"
                  value={uploadForm.name}
                  onChange={(e) => handleUploadFormChange('name', e.target.value)}
                  placeholder="Enter audio name"
                  required
                  disabled={isUploading}
                />
              </FormField>

              <FormField>
                <Label htmlFor="audioType">Type *</Label>
                <Select
                  id="audioType"
                  value={uploadForm.audio_type}
                  onChange={(e) => handleUploadFormChange('audio_type', e.target.value)}
                  required
                  disabled={isUploading}
                >
                  <option value="music">Music</option>
                  <option value="background">Background/Ambient</option>
                  <option value="sound">Sound Effects</option>
                </Select>
              </FormField>

              <FormField>
                <Label htmlFor="audioDescription">Description</Label>
                <TextArea
                  id="audioDescription"
                  value={uploadForm.description}
                  onChange={(e) => handleUploadFormChange('description', e.target.value)}
                  placeholder="Brief description of the audio"
                  disabled={isUploading}
                />
              </FormField>

              {user?.user_permissions?.private_assets && (
                <CheckboxField>
                  <CheckboxInput
                    type="checkbox"
                    id="isPrivate"
                    checked={uploadForm.is_private}
                    onChange={(e) => handleUploadFormChange('is_private', e.target.checked)}
                    disabled={isUploading}
                  />
                  <CheckboxLabel htmlFor="isPrivate">
                    Keep this audio private (only you can use it)
                  </CheckboxLabel>
                </CheckboxField>
              )}

              <CheckboxField>
                <CheckboxInput
                  type="checkbox"
                  id="copyrightConfirm"
                  checked={uploadForm.copyright_confirmation}
                  onChange={(e) => handleUploadFormChange('copyright_confirmation', e.target.checked)}
                  disabled={isUploading}
                  required
                />
                <CheckboxLabel htmlFor="copyrightConfirm">
                  * I confirm I have full rights to this audio and grant YouMeditation permission to use it as specified by my privacy settings and Terms of Service.
                </CheckboxLabel>
              </CheckboxField>

              <ModalActions>
                <SecondaryButton 
                  type="button" 
                  onClick={handleCloseUploadModal}
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={isUploading || !uploadForm.file}>
                  {isUploading ? 'Uploading...' : 'Upload Audio'}
                </PrimaryButton>
              </ModalActions>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default Audio; 